import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { asArray, fetchWithAuth, fetchWithCorpId, fetchWithCorpIdAndFormData } from '../utils/api';
import { AUTH_URL } from '../config';
import { useAuth } from '../utils/auth';
import { Section, Field, FormLoading } from './ui/formkit';
import { useToast } from './ui/Toast';
import { useConfirm } from './ui/ConfirmDialog';
import PageHeader from './ui/PageHeader';
import InfoTip from './ui/InfoTip';
import Drawer from './Drawer';
import AdminForm from './AdminForm';
import ChangePasswordForm from './ChangePasswordForm';

// Settings screen reached from the top-bar user button (issue #101).
// User-level tabs (Profile, Notifications, Security) for everyone; the
// Corporation tab (admin and super only, #118) holds the corp logo, the
// invoicing settings, and the advanced raw /admin config rows.
// Backend contract: vms-backend#114 (/profile, /profile/notifications)
// and vms-backend#118 (username, corpName, /profile/avatar).

const READONLY_INPUT = {
  background: 'var(--surface-soft)', cursor: 'not-allowed', color: 'var(--muted)',
};

const roleLabelFor = (roles) => roles.includes('super') ? 'Super Admin'
  : roles.includes('admin') ? 'Admin'
    : roles.includes('employer') ? 'Employer'
      : roles.includes('employee') ? 'Employee'
        : 'User';

/* ── Profile tab ─────────────────────────────────────────────── */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mirrors the backend rule (vms-backend#118): 3-30 characters, starting with
// a letter or number, then letters, numbers, dots, dashes, or underscores.
const USERNAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{2,29}$/;
const USERNAME_RULE = 'Username must be 3-30 characters using letters, numbers, dots, dashes, or underscores.';

const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_TYPES = ['image/png', 'image/jpeg'];

// "john.doe" → "JD", "amy" → "A". Splits on the separators usernames allow.
const initialsFor = (name) => {
  const parts = String(name || '').split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  return parts.slice(0, 2).map(p => p.charAt(0)).join('').toUpperCase();
};

const ProfileTab = () => {
  const toast = useToast();
  const { getUserRoles } = useAuth();
  const [profile, setProfile] = useState({ clientId: '', username: '', email: '', phone: '', corpName: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarError, setAvatarError] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [removingAvatar, setRemovingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const avatarBusy = uploadingAvatar || removingAvatar;

  const loadAvatar = useCallback(async () => {
    try {
      const response = await fetchWithCorpId('/profile/avatar', { headers: { Accept: 'image/*' }, silent: true });
      if (!response.ok) { setAvatarUrl(null); return; }
      const blob = await response.blob();
      setAvatarUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    } catch { setAvatarUrl(null); /* 404 when none, or older backend */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchWithCorpId('/profile');
        if (!cancelled && data) {
          setProfile({
            clientId: data.clientId || '',
            username: data.username || '',
            email: data.email || '',
            phone: data.phone || '',
            corpName: data.corpName || '',
            roles: data.roles,
          });
        }
      } catch (e) {
        if (!cancelled) setError('Could not load your profile: ' + e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    loadAvatar();
    return () => { cancelled = true; };
  }, [loadAvatar]);

  // Revoke the last object URL when the tab unmounts.
  useEffect(() => () => { setAvatarUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarFile = async (file) => {
    if (!file) return;
    setAvatarError('');
    if (!AVATAR_TYPES.includes(file.type)) {
      setAvatarError('Choose a PNG or JPEG image.');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError('Choose an image up to 2 MB.');
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await fetchWithCorpIdAndFormData('/profile/avatar', fd, { method: 'PUT' });
      await loadAvatar();
      toast.success('Photo updated.');
    } catch (e) {
      setAvatarError(e.message || 'Failed to upload the photo.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarUploadClick = () => {
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
      avatarInputRef.current.click();
    }
  };

  const handleAvatarRemove = async () => {
    if (avatarBusy) return;
    setAvatarError('');
    setRemovingAvatar(true);
    try {
      await fetchWithCorpId('/profile/avatar', { method: 'DELETE' });
      setAvatarUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
      toast.success('Photo removed.');
    } catch (e) {
      setAvatarError(e.message || 'Failed to remove the photo.');
    } finally {
      setRemovingAvatar(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError('');
    // An empty username means "not set" and stays allowed (the backend
    // normalizes it to null, vms-backend#118), so existing users can still
    // save email and phone. Validate only when one is entered.
    const username = profile.username.trim();
    if (username && !USERNAME_PATTERN.test(username)) {
      setError(USERNAME_RULE);
      return;
    }
    if (profile.email && !EMAIL_PATTERN.test(profile.email)) {
      setError('Enter a valid email address.');
      return;
    }
    setSaving(true);
    try {
      await fetchWithCorpId('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          email: profile.email,
          phone: profile.phone,
        }),
      });
      setProfile(prev => ({ ...prev, username }));
      toast.success('Profile saved.');
    } catch (err) {
      // Surfaces the API's messages verbatim, e.g. "That username is taken."
      // or "That email is already in use."
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <FormLoading label="Loading profile…" />;

  const roleLabel = roleLabelFor(Array.isArray(profile.roles) ? profile.roles : getUserRoles());

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>{error}</div>
      )}

      <Section title="Photo" description="Shown next to your name in the top bar." grid={null}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%', flexShrink: 0,
            overflow: 'hidden', background: 'var(--coral)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="Your profile photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{
                fontFamily: 'var(--font-sans)', fontSize: '20px', fontWeight: 500,
                color: 'var(--on-primary)', letterSpacing: '0.5px',
              }}>{initialsFor(profile.username || profile.clientId)}</span>
            )}
          </div>
          <div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn-secondary" onClick={handleAvatarUploadClick} disabled={avatarBusy}>
                {uploadingAvatar ? 'Uploading…' : 'Upload photo'}
              </button>
              {avatarUrl && (
                <button type="button" className="btn-secondary" onClick={handleAvatarRemove} disabled={avatarBusy}>
                  {removingAvatar ? 'Removing…' : 'Remove'}
                </button>
              )}
            </div>
            <div style={{
              marginTop: '6px', fontFamily: 'var(--font-sans)',
              fontSize: '12.5px', color: 'var(--muted-soft)',
            }}>
              PNG or JPEG, up to 2 MB.
            </div>
          </div>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(e) => handleAvatarFile(e.target.files && e.target.files[0])}
          />
        </div>
        {avatarError && (
          <div className="alert error" role="alert" style={{ marginTop: '12px' }}>{avatarError}</div>
        )}
      </Section>

      <Section title="Profile" description="How your name and contact details appear across One Nexus.">
        <Field label="Username" span={2} hint="You can sign in with this. 3 to 30 characters: letters, numbers, dots, dashes, underscores.">
          <input type="text" name="username" value={profile.username} onChange={handleChange} maxLength={30} />
        </Field>
        <Field label="Email">
          <input type="email" name="email" value={profile.email} onChange={handleChange} />
        </Field>
        <Field label="Phone">
          <input type="tel" name="phone" value={profile.phone} onChange={handleChange} />
        </Field>
      </Section>

      <Section title="Account" description="Managed by your administrator. Shown here for reference.">
        <Field label="Corporation">
          <input type="text" value={profile.corpName || ''} readOnly style={READONLY_INPUT} />
        </Field>
        <Field label="Role">
          <input type="text" value={roleLabel} readOnly style={READONLY_INPUT} />
        </Field>
        <Field label="Sign-in code" hint="You can sign in with this, your username, or your email.">
          <input type="text" value={profile.clientId || ''} readOnly style={READONLY_INPUT} />
        </Field>
      </Section>

      <div>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
};

/* ── Notifications tab ───────────────────────────────────────── */

// Plain-language labels per event type, grouped by area. Types the server
// returns that are not listed here fall into "Other" with a prettified name.
const NOTIFICATION_GROUPS = [
  {
    title: 'Timesheets',
    labels: {
      TIMESHEET_SUBMITTED: 'A timesheet is submitted for review',
      TIMESHEET_APPROVED: 'Your timesheet is approved',
      TIMESHEET_REJECTED: 'Your timesheet is returned for changes',
      TIMESHEET_REMINDER: 'A reminder that your timesheet is due',
      TIMESHEET_DUE_SOON: 'A heads up before your timesheet is due',
    },
  },
  {
    title: 'Expenses',
    labels: {
      EXPENSE_SUBMITTED: 'An expense is submitted for review',
      EXPENSE_APPROVED: 'Your expense is approved',
      EXPENSE_REJECTED: 'Your expense is rejected',
    },
  },
  {
    title: 'Invoices',
    labels: {
      INVOICE_SENT: 'An invoice is emailed',
      INVOICE_PAID: 'An invoice is marked paid',
    },
  },
  {
    title: 'Documents',
    labels: {
      DOCUMENT_UPLOADED: 'An employee uploads a document',
    },
  },
];

// TIMESHEET_SUBMITTED → "Timesheet submitted"
const prettifyType = (type) => {
  const words = String(type).toLowerCase().split('_').filter(Boolean);
  const sentence = words.join(' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

const GROUP_HEADING_STYLE = {
  fontFamily: 'var(--font-sans)',
  fontSize: '11px', fontWeight: 500, color: 'var(--muted)',
  letterSpacing: '1.4px', textTransform: 'uppercase',
  margin: '0 0 10px',
};

const NotificationsTab = () => {
  const toast = useToast();
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchWithCorpId('/profile/notifications');
        if (!cancelled) setPrefs(asArray(data));
      } catch (e) {
        if (!cancelled) setError('Could not load your notification settings: ' + e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const toggle = (type) => setPrefs(prev =>
    prev.map(p => p.type === type ? { ...p, enabled: !p.enabled } : p)
  );

  const handleSave = async () => {
    if (saving) return;
    setError('');
    setSaving(true);
    try {
      await fetchWithCorpId('/profile/notifications', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs),
      });
      toast.success('Notification settings saved.');
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <FormLoading label="Loading notification settings…" />;

  const byType = new Map(prefs.map(p => [p.type, p]));
  const labeledTypes = new Set(NOTIFICATION_GROUPS.flatMap(g => Object.keys(g.labels)));
  const groups = NOTIFICATION_GROUPS
    .map(g => ({
      title: g.title,
      rows: Object.entries(g.labels)
        .filter(([type]) => byType.has(type))
        .map(([type, label]) => ({ type, label, enabled: byType.get(type).enabled })),
    }))
    .filter(g => g.rows.length > 0);
  const otherRows = prefs
    .filter(p => !labeledTypes.has(p.type))
    .map(p => ({ type: p.type, label: prettifyType(p.type), enabled: p.enabled }));
  if (otherRows.length > 0) groups.push({ title: 'Other', rows: otherRows });

  return (
    <div>
      {error && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>{error}</div>
      )}
      <Section
        title="Notification preferences"
        description="These control the in-app notifications you receive."
        grid={null}
      >
        {groups.length === 0 ? (
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', color: 'var(--muted)', margin: 0 }}>
            No notification settings are available yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {groups.map(group => (
              <div key={group.title}>
                <h3 style={GROUP_HEADING_STYLE}>{group.title}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {group.rows.map(row => (
                    <label key={row.type} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      fontFamily: 'var(--font-sans)', fontSize: '14px', fontWeight: 400,
                      color: 'var(--body-text)', textTransform: 'none', letterSpacing: 0,
                      cursor: 'pointer', margin: 0,
                    }}>
                      <input
                        type="checkbox"
                        checked={!!row.enabled}
                        onChange={() => toggle(row.type)}
                        style={{ width: '16px', height: '16px', margin: 0, flexShrink: 0 }}
                      />
                      {row.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
      {groups.length > 0 && (
        <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  );
};

/* ── Corporation tab (admin and super only, #118) ────────────── */

const INV_INIT_FEATURE = 'INV_NBR_INIT';
const INV_BY_CLIENT_FEATURE = 'INV_NBR_BY_CLIENT';
const INVOICING_FEATURES = [INV_INIT_FEATURE, INV_BY_CLIENT_FEATURE];

// Timesheet rows managed by the Timesheets card (#128, backend vms-backend#127).
const TS_REMINDERS_FEATURE = 'timesheet.reminders.enabled';
const TS_REMINDER_DAYS_FEATURE = 'timesheet.reminders.days';
const TS_AUTOMATED_FEATURE = 'timesheet.flow.automated';
// Heads-up reminders before the period ends (#130, backend vms-backend#129).
const TS_HEADS_UP_FEATURE = 'timesheet.reminders.heads-up-days';
const TIMESHEET_FEATURES = [TS_REMINDERS_FEATURE, TS_REMINDER_DAYS_FEATURE, TS_AUTOMATED_FEATURE, TS_HEADS_UP_FEATURE];

// Chip presets for the heads-up days, largest first.
const TS_HEADS_UP_PRESETS = [14, 7, 5, 3, 2, 1];

// "5,1" → [5, 1]. Keeps any integer 1-30, so values without a chip round-trip.
const parseHeadsUpDays = (value) => {
  const seen = new Set();
  String(value == null ? '' : value)
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => Number.isInteger(n) && n >= 1 && n <= 30)
    .forEach(n => seen.add(n));
  return [...seen];
};

// Rows owned by a dedicated card, so they stay out of the Advanced list.
const MANAGED_FEATURES = [...INVOICING_FEATURES, ...TIMESHEET_FEATURES];

const CHECK_LABEL_STYLE = {
  display: 'flex', alignItems: 'center', gap: '10px',
  fontFamily: 'var(--font-sans)', fontSize: '14px', fontWeight: 400,
  color: 'var(--body-text)', textTransform: 'none', letterSpacing: 0,
  cursor: 'pointer', margin: 0,
};

const CHECKBOX_STYLE = { width: '16px', height: '16px', margin: 0, flexShrink: 0 };

const CorporationTab = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const corpId = localStorage.getItem('selectedCorpId');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  // The input is kept as a string so it can be cleared.
  const [invInit, setInvInit] = useState('');
  const [invByClient, setInvByClient] = useState(false);
  const [savingInvoicing, setSavingInvoicing] = useState(false);

  // Timesheets card. Reminders: missing row = on. Automation: missing row = off.
  const [tsReminders, setTsReminders] = useState(true);
  const [tsReminderDays, setTsReminderDays] = useState('');
  const [tsDaysError, setTsDaysError] = useState('');
  const [tsAutomated, setTsAutomated] = useState(false);
  // Heads-up days as an array of ints; missing row = off (empty).
  const [tsHeadsUpDays, setTsHeadsUpDays] = useState([]);
  const [savingTimesheets, setSavingTimesheets] = useState(false);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [drawer, setDrawer] = useState(null); // null | { mode: 'create' } | { mode: 'edit', id }
  const formRef = useRef(null);

  const rowFor = (feature) => rows.find(r => r.feature === feature) || null;
  const upsertRow = (row) => {
    if (!row || !row.id) return;
    setRows(prev => prev.some(r => r.id === row.id)
      ? prev.map(r => r.id === row.id ? row : r)
      : [...prev, row]);
  };
  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  const loadLogo = useCallback(async () => {
    if (!corpId) return;
    try {
      const response = await fetchWithAuth(`/corps/${corpId}/logo`, { headers: { Accept: 'image/*' }, silent: true });
      if (!response.ok) { setLogoUrl(null); return; }
      const blob = await response.blob();
      setLogoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    } catch { setLogoUrl(null); }
  }, [corpId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!corpId) { setLoading(false); return; }
      try {
        const adminRows = await fetchWithCorpId('/admin?page=0&size=100000');
        if (cancelled) return;
        const all = asArray(adminRows);
        setRows(all);
        const initRow = all.find(r => r.feature === INV_INIT_FEATURE);
        setInvInit(initRow && initRow.value != null ? String(initRow.value) : '');
        const byClientRow = all.find(r => r.feature === INV_BY_CLIENT_FEATURE);
        setInvByClient(byClientRow ? byClientRow.value === 'Y' : false);
        const remindersRow = all.find(r => r.feature === TS_REMINDERS_FEATURE);
        setTsReminders(remindersRow ? remindersRow.value !== 'false' : true);
        const daysRow = all.find(r => r.feature === TS_REMINDER_DAYS_FEATURE);
        setTsReminderDays(daysRow && daysRow.value != null ? String(daysRow.value) : '');
        const automatedRow = all.find(r => r.feature === TS_AUTOMATED_FEATURE);
        setTsAutomated(automatedRow ? automatedRow.value === 'true' : false);
        const headsUpRow = all.find(r => r.feature === TS_HEADS_UP_FEATURE);
        setTsHeadsUpDays(headsUpRow ? parseHeadsUpDays(headsUpRow.value) : []);
      } catch (e) {
        if (!cancelled) setError('Could not load corporation settings: ' + e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    loadLogo();
    return () => { cancelled = true; };
  }, [corpId, loadLogo]);

  // Revoke the last object URL when the tab unmounts.
  useEffect(() => () => { setLogoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); }, []);

  /* Logo */

  const handleLogoFile = async (file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Choose an image file.');
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      await fetchWithCorpIdAndFormData(`/corps/${corpId}/logo`, fd, { method: 'PUT' });
      toast.success('Logo updated.');
      await new Promise(r => setTimeout(r, 500));
      await loadLogo();
    } catch (e) {
      toast.error(e.message || 'Failed to upload logo.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  /* Row writes */

  const commonFields = () => ({
    corpId: localStorage.getItem('selectedCorpId') || '0',
    updateDate: new Date().toISOString(),
  });

  const updateRowValue = async (row, value) => {
    await fetchWithCorpId(`/admin/${row.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...row, value, ...commonFields() }),
    });
    upsertRow({ ...row, value });
  };

  const createRow = async ({ category, feature, description, value }) => {
    const created = await fetchWithCorpId('/admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, feature, description, value, ...commonFields() }),
    });
    if (created && created.id) upsertRow(created);
  };

  const deleteRow = async (row) => {
    await fetchWithCorpId(`/admin/${row.id}`, { method: 'DELETE' });
    removeRow(row.id);
  };

  const handleSaveInvoicing = async () => {
    if (savingInvoicing) return;
    const trimmed = invInit.trim();
    if (trimmed !== '' && (!/^\d+$/.test(trimmed) || parseInt(trimmed, 10) < 1)) {
      toast.error('Starting invoice number must be a whole number of 1 or more, or empty.');
      return;
    }
    setError('');
    setSavingInvoicing(true);
    try {
      // Starting invoice number: empty input removes the row (default: 1).
      const initRow = rowFor(INV_INIT_FEATURE);
      if (trimmed === '') {
        if (initRow) await deleteRow(initRow);
      } else if (initRow) {
        await updateRowValue(initRow, trimmed);
      } else {
        await createRow({
          category: 'invoicing',
          feature: INV_INIT_FEATURE,
          description: 'Number the first invoice of this corporation starts at',
          value: trimmed,
        });
      }
      // Per-client numbering: missing row = off, so only create when turning on.
      const byClientRow = rowFor(INV_BY_CLIENT_FEATURE);
      if (byClientRow) {
        await updateRowValue(byClientRow, invByClient ? 'Y' : 'N');
      } else if (invByClient) {
        await createRow({
          category: 'invoicing',
          feature: INV_BY_CLIENT_FEATURE,
          description: 'Y = each client has its own invoice number sequence',
          value: 'Y',
        });
      }
      toast.success('Invoicing settings saved.');
    } catch (e) {
      setError(e.message || 'Failed to save. Please try again.');
    } finally {
      setSavingInvoicing(false);
    }
  };

  const toggleHeadsUpDay = (day) => setTsHeadsUpDays(prev =>
    prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);

  const handleSaveTimesheets = async () => {
    if (savingTimesheets) return;
    const days = tsReminderDays.trim();
    if (days !== '' && (!/^\d+$/.test(days) || parseInt(days, 10) < 1 || parseInt(days, 10) > 30)) {
      setTsDaysError('Reminder days must be between 1 and 30.');
      return;
    }
    setTsDaysError('');
    setError('');
    setSavingTimesheets(true);
    try {
      // Reminders: missing row = on, so create with "false" only when switching off.
      const remindersRow = rowFor(TS_REMINDERS_FEATURE);
      if (remindersRow) {
        await updateRowValue(remindersRow, tsReminders ? 'true' : 'false');
      } else if (!tsReminders) {
        await createRow({
          category: 'timesheets',
          feature: TS_REMINDERS_FEATURE,
          description: 'Corp-wide switch for timesheet reminder emails and notifications',
          value: 'false',
        });
      }
      // Reminder cadence: empty input removes the row (default: every 3 days).
      const daysRow = rowFor(TS_REMINDER_DAYS_FEATURE);
      if (days === '') {
        if (daysRow) await deleteRow(daysRow);
      } else if (daysRow) {
        await updateRowValue(daysRow, days);
      } else {
        await createRow({
          category: 'timesheets',
          feature: TS_REMINDER_DAYS_FEATURE,
          description: 'Days between reminders for an overdue timesheet',
          value: days,
        });
      }
      // Heads-up days: empty selection removes the row (default: off).
      const headsUpRow = rowFor(TS_HEADS_UP_FEATURE);
      const headsUpCsv = [...tsHeadsUpDays].sort((a, b) => b - a).join(',');
      if (tsHeadsUpDays.length === 0) {
        if (headsUpRow) await deleteRow(headsUpRow);
      } else if (headsUpRow) {
        await updateRowValue(headsUpRow, headsUpCsv);
      } else {
        await createRow({
          category: 'timesheets',
          feature: TS_HEADS_UP_FEATURE,
          description: 'Days before the period ends to send a heads up, comma separated',
          value: headsUpCsv,
        });
      }
      // Automation: missing row = off, so only create when switching on.
      const automatedRow = rowFor(TS_AUTOMATED_FEATURE);
      if (automatedRow) {
        await updateRowValue(automatedRow, tsAutomated ? 'true' : 'false');
      } else if (tsAutomated) {
        await createRow({
          category: 'timesheets',
          feature: TS_AUTOMATED_FEATURE,
          description: 'true = approve submitted timesheets and send their invoices automatically',
          value: 'true',
        });
      }
      toast.success('Timesheet settings saved.');
    } catch (e) {
      setError(e.message || 'Failed to save. Please try again.');
    } finally {
      setSavingTimesheets(false);
    }
  };

  /* Advanced configuration */

  const refreshRows = async () => {
    try {
      const data = await fetchWithCorpId('/admin?page=0&size=100000');
      setRows(asArray(data));
    } catch (e) {
      setError('Could not reload settings: ' + e.message);
    }
  };

  const openCreate = () => setDrawer({ mode: 'create' });
  const openEdit = (row) => setDrawer({ mode: 'edit', id: row.id });
  const closeDrawer = () => setDrawer(null);
  const handleDrawerSuccess = () => { closeDrawer(); refreshRows(); };

  const handleDeleteAdvanced = async (row) => {
    const ok = await confirm({
      title: 'Delete setting',
      message: `Delete ${row.feature}? The system falls back to its default behavior.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteRow(row);
      toast.success('Setting deleted.');
    } catch (e) {
      toast.error(e.message || 'Failed to delete the setting.');
    }
  };

  const advancedRows = rows.filter(r => !MANAGED_FEATURES.includes(r.feature));

  /* Render */

  if (!corpId) {
    return (
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', color: 'var(--muted)', margin: 0 }}>
        Select a corporation first to manage its settings.
      </p>
    );
  }

  if (loading) return <FormLoading label="Loading corporation settings…" />;

  return (
    <div>
      {error && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>{error}</div>
      )}

      <Section title="Logo" description="Shown in the top bar for everyone in this corporation." grid={null}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '56px', height: '56px', flexShrink: 0,
            border: '1px dashed var(--hairline)', borderRadius: 'var(--radius-md)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', background: 'var(--surface-soft)',
          }}>
            {logoUrl ? (
              <img src={logoUrl} alt="Corporation logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: '11px', color: 'var(--muted-soft)' }}>No logo</span>
            )}
          </div>
          <div>
            <button type="button" className="btn-secondary" onClick={handleLogoClick} disabled={uploadingLogo}>
              {uploadingLogo ? 'Uploading…' : 'Upload logo'}
            </button>
            <div style={{
              marginTop: '6px', fontFamily: 'var(--font-sans)',
              fontSize: '12.5px', color: 'var(--muted-soft)',
            }}>
              PNG or JPG works best.
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => handleLogoFile(e.target.files && e.target.files[0])}
          />
        </div>
      </Section>

      <Section title="Invoicing" grid={null}>
        <Field
          label={(
            <>
              Starting invoice number
              <InfoTip tip="The number this corporation's first invoice starts at. Applies only until the first invoice exists. Empty means start at 1." />
            </>
          )}
        >
          <input
            type="number"
            min="1"
            step="1"
            value={invInit}
            onChange={(e) => setInvInit(e.target.value)}
            style={{ maxWidth: '220px' }}
          />
        </Field>
        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center' }}>
          <label style={CHECK_LABEL_STYLE}>
            <input
              type="checkbox"
              checked={invByClient}
              onChange={() => setInvByClient(v => !v)}
              style={CHECKBOX_STYLE}
            />
            Number invoices per client
          </label>
          <InfoTip tip="On: each client gets its own invoice number sequence. Off: one sequence for the whole corporation." />
        </div>
        <div style={{ marginTop: '20px' }}>
          <button type="button" className="btn-primary" onClick={handleSaveInvoicing} disabled={savingInvoicing}>
            {savingInvoicing ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Section>

      <Section title="Timesheets" grid={null}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <label style={CHECK_LABEL_STYLE}>
            <input
              type="checkbox"
              checked={tsReminders}
              onChange={() => setTsReminders(v => !v)}
              style={CHECKBOX_STYLE}
            />
            Timesheet reminders
          </label>
          <InfoTip tip="Daily check that reminds employees with an overdue timesheet, by email and in-app." />
        </div>
        <div style={{
          marginTop: '10px', marginLeft: '26px',
          display: 'flex', alignItems: 'center', gap: '8px',
          fontFamily: 'var(--font-sans)', fontSize: '14px',
          color: tsReminders ? 'var(--body-text)' : 'var(--muted-soft)',
        }}>
          Remind every
          <input
            type="number"
            min="1"
            max="30"
            step="1"
            placeholder="3"
            value={tsReminderDays}
            disabled={!tsReminders}
            onChange={(e) => { setTsReminderDays(e.target.value); setTsDaysError(''); }}
            aria-label="Remind every N days"
            style={{ width: '80px' }}
          />
          days
          <InfoTip tip="How often an employee with an overdue timesheet is reminded. Default is every 3 days." />
        </div>
        {tsDaysError && (
          <div role="alert" style={{
            marginTop: '6px', marginLeft: '26px',
            fontFamily: 'var(--font-sans)', fontSize: '12.5px', color: 'var(--danger)',
          }}>
            {tsDaysError}
          </div>
        )}
        <div style={{
          marginTop: '14px', marginLeft: '26px',
          display: 'flex', alignItems: 'center',
          fontFamily: 'var(--font-sans)', fontSize: '14px',
          color: tsReminders ? 'var(--body-text)' : 'var(--muted-soft)',
        }}>
          Heads up before the period ends
          <InfoTip tip="Sends employees a reminder before their timesheet period ends, on each selected day. Example: 5 and 1 means five days before and again one day before." />
        </div>
        <div style={{
          marginTop: '8px', marginLeft: '26px',
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px',
        }}>
          {TS_HEADS_UP_PRESETS.map(day => {
            const selected = tsHeadsUpDays.includes(day);
            return (
              <button
                key={day}
                type="button"
                aria-pressed={selected}
                aria-label={`${day} days before the period ends`}
                disabled={!tsReminders}
                onClick={() => toggleHeadsUpDay(day)}
                style={{
                  fontFamily: 'var(--font-sans)', fontSize: '13px', fontWeight: 500,
                  lineHeight: 1, padding: '6px 13px',
                  borderRadius: 'var(--radius-pill)',
                  border: `1px solid ${selected ? 'var(--coral)' : 'var(--hairline)'}`,
                  background: selected ? 'var(--coral-tint)' : 'transparent',
                  color: !tsReminders ? 'var(--muted-soft)'
                    : selected ? 'var(--coral-active)' : 'var(--muted)',
                  cursor: tsReminders ? 'pointer' : 'default',
                  opacity: tsReminders ? 1 : 0.6,
                }}
              >
                {day}
              </button>
            );
          })}
          <span style={{
            fontFamily: 'var(--font-sans)', fontSize: '12.5px',
            color: tsReminders ? 'var(--muted)' : 'var(--muted-soft)',
          }}>
            days before the period ends
          </span>
        </div>
        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center' }}>
          <label style={CHECK_LABEL_STYLE}>
            <input
              type="checkbox"
              checked={tsAutomated}
              onChange={() => setTsAutomated(v => !v)}
              style={CHECKBOX_STYLE}
            />
            Automate the timesheet flow
          </label>
          <InfoTip tip="When on: a submitted timesheet is approved automatically, its invoice is created and emailed to the client's Billing contacts. Staff can review or change anything at any point. When off: staff approve and send manually." />
        </div>
        <div style={{ marginTop: '20px' }}>
          <button type="button" className="btn-primary" onClick={handleSaveTimesheets} disabled={savingTimesheets}>
            {savingTimesheets ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Section>

      <Section
        title="Advanced configuration"
        description="Raw configuration rows read by the system. Most corporations never need these."
        grid={null}
      >
        <button
          type="button"
          className="btn-secondary"
          aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen(o => !o)}
        >
          {advancedOpen ? 'Hide advanced configuration' : 'Show advanced configuration'}
        </button>

        {advancedOpen && (
          <div style={{ marginTop: '16px' }}>
            {advancedRows.length === 0 ? (
              <p style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', color: 'var(--muted)', margin: 0 }}>
                No advanced configuration rows.
              </p>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Feature</th>
                      <th>Description</th>
                      <th>Value</th>
                      <th style={{ width: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advancedRows.map(row => (
                      <tr key={row.id}>
                        <td>{row.category}</td>
                        <td>
                          <button type="button" className="btn-link table-link" onClick={() => openEdit(row)} title="Open setting">
                            {row.feature}
                          </button>
                        </td>
                        <td style={{ color: 'var(--muted)', maxWidth: '280px' }}>{row.description}</td>
                        <td>
                          <code className="code-pill">{row.value}</code>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <button type="button" className="btn-link" onClick={() => openEdit(row)}>Edit</button>
                            <button
                              type="button"
                              className="btn-link"
                              style={{ color: 'var(--danger)' }}
                              onClick={() => handleDeleteAdvanced(row)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ marginTop: '16px' }}>
              <button type="button" className="btn-secondary" onClick={openCreate}>New setting</button>
            </div>
          </div>
        )}
      </Section>

      <Drawer
        open={drawer !== null}
        onClose={closeDrawer}
        title={drawer?.mode === 'edit' ? 'Edit setting' : 'New setting'}
        subtitle={drawer?.mode === 'edit' ? 'Update the value for this feature. Other fields are read-only.' : 'Add a new configuration setting.'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
              {drawer?.mode === 'edit' ? 'Save changes' : 'Create setting'}
            </button>
          </>
        }
      >
        {drawer && (
          <AdminForm
            key={drawer.id || 'new'}
            mode={drawer.mode}
            adminId={drawer.id}
            formRef={formRef}
            hideActions
            hideChrome
            onSuccess={handleDrawerSuccess}
          />
        )}
      </Drawer>
    </div>
  );
};

/* ── About block ─────────────────────────────────────────────── */

// Quiet one-line version readout at the bottom of Settings (#150). The UI
// version and commit are baked in at build time by the deploy workflow
// (REACT_APP_VERSION / REACT_APP_COMMIT); the backend and auth versions come
// from their /version endpoints (backend #135, auth #43). Each fetch is
// silent and best-effort: if a service or field is unavailable, that part of
// the line is simply omitted.
const AboutVersions = () => {
  const [apiInfo, setApiInfo] = useState(null);
  const [authInfo, setAuthInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchWithCorpId('/version', { silent: true })
      .then(data => {
        if (!cancelled && data && data.version) setApiInfo(data);
      })
      .catch(() => { /* show nothing for the API version */ });
    fetch(`${AUTH_URL}/version`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!cancelled && data && data.version) setAuthInfo(data);
      })
      .catch(() => { /* show nothing for the auth version */ });
    return () => { cancelled = true; };
  }, []);

  const uiVersion = process.env.REACT_APP_VERSION || 'dev';
  const uiCommit = process.env.REACT_APP_COMMIT;

  const parts = [`One Nexus ${uiVersion}`];
  if (apiInfo) parts.push(`API ${apiInfo.version}`);
  if (authInfo) parts.push(`Auth ${authInfo.version}`);

  const commitParts = [];
  if (uiCommit) commitParts.push(`UI ${uiCommit}`);
  if (apiInfo?.commit) commitParts.push(`API ${apiInfo.commit}`);
  if (authInfo?.commit) commitParts.push(`Auth ${authInfo.commit}`);

  return (
    <div
      style={{ marginTop: '16px', fontSize: '12px', color: 'var(--muted)' }}
      title={commitParts.length ? commitParts.join(' · ') : undefined}
    >
      {parts.join(' · ')}
    </div>
  );
};

/* ── Page shell ──────────────────────────────────────────────── */

const Settings = () => {
  const { hasRole, isSuperUser } = useAuth();
  const canManageCorp = hasRole('admin') || isSuperUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = [
    { key: 'profile', label: 'Profile' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'security', label: 'Security' },
    ...(canManageCorp ? [{ key: 'corporation', label: 'Corporation' }] : []),
  ];

  const requested = searchParams.get('tab');
  const activeTab = tabs.some(t => t.key === requested) ? requested : 'profile';
  const selectTab = (key) => setSearchParams({ tab: key }, { replace: true });

  // Same sub-tab treatment as ClientForm.
  const tabStyle = (tab) => ({
    padding: '10px 18px',
    cursor: 'pointer',
    background: activeTab === tab ? 'var(--surface-card)' : 'transparent',
    color: activeTab === tab ? 'var(--ink)' : 'var(--muted)',
    border: 'none',
    borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
    fontFamily: 'var(--font-sans)',
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: 0,
    transition: 'background 0.16s, color 0.16s',
  });

  return (
    <div className="page-container">
      <PageHeader
        title="Settings"
        subtitle="Manage your profile, notifications, and security."
      />
      <div className="card" style={{ maxWidth: '760px' }}>
        <div className="card-body">
          <div role="tablist" style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--hairline)', marginBottom: '24px' }}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                style={tabStyle(tab.key)}
                onClick={() => selectTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'profile' && <ProfileTab />}
          {activeTab === 'notifications' && <NotificationsTab />}
          {activeTab === 'security' && (
            <Section title="Password" description="Change the password you use to sign in." grid={null}>
              <ChangePasswordForm />
            </Section>
          )}
          {activeTab === 'corporation' && canManageCorp && <CorporationTab />}
        </div>
      </div>
      <AboutVersions />
    </div>
  );
};

export default Settings;
